import { useState, useEffect, useRef, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getUnitDisplayLabel } from "@/lib/propertyUnitMatrix";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Upload, Loader2, X, FileText, Image as ImageIcon } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useProperties } from "@/hooks/useProperties";
import { useUnits } from "@/hooks/useUnits";
import { useContracts } from "@/hooks/useContracts";
import { useCreateExpense, useUpdateExpense, type Expense } from "@/hooks/useFinance";
import { uploadDocument, uploadImages, resolveDocumentUrl, getStorageErrorMessage, isExpectedUploadError, IMAGE_ACCEPT, IMAGE_OR_DOCUMENT_ACCEPT } from "@/lib/storage";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { ExpenseType, PaymentResponsibility } from "@/types/database";

interface AddExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPropertyId?: string;
  defaultUnitId?: string;
  expense?: Expense;
}

const EXPENSE_TYPES: { value: string; labelAr: string; labelEn: string }[] = [
  { value: "maintenance", labelAr: "صيانة", labelEn: "Maintenance" },
  { value: "electricity", labelAr: "كهرباء", labelEn: "Electricity" },
  { value: "water", labelAr: "مياه", labelEn: "Water" },
  { value: "gas", labelAr: "غاز", labelEn: "Gas" },
  { value: "insurance", labelAr: "تأمين", labelEn: "Insurance" },
  { value: "marketing", labelAr: "تسويق", labelEn: "Marketing" },
  { value: "taxes", labelAr: "ضرائب", labelEn: "Taxes" },
  { value: "other", labelAr: "أخرى", labelEn: "Other" },
];

const PAYMENT_RESPONSIBILITIES: { value: PaymentResponsibility; labelAr: string; labelEn: string }[] = [
  { value: "owner", labelAr: "المالك", labelEn: "Owner" },
  { value: "tenant", labelAr: "المستأجر", labelEn: "Tenant" },
  { value: "company", labelAr: "الشركة", labelEn: "Company" },
];

export function AddExpenseDialog({ open, onOpenChange, defaultPropertyId, defaultUnitId, expense }: AddExpenseDialogProps) {
  const { t, language } = useLanguage();
  const { companyId } = useAuth();
  const isEdit = !!expense;
  const [date, setDate] = useState<Date>(new Date());
  const [includeVat, setIncludeVat] = useState(false);
  // Tracks the actual VAT rate entered. Defaults to 15% (Saudi standard rate).
  // In edit mode, prefilled from the existing expense (so saving keeps the rate
  // that was originally stored rather than always overwriting with 15).
  const [vatRateValue, setVatRateValue] = useState(15);

  // File upload states
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string>("");
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isDraggingInvoice, setIsDraggingInvoice] = useState(false);
  const [isDraggingImages, setIsDraggingImages] = useState(false);
  
  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const imagesInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState({
    property_id: defaultPropertyId || "",
    unit_id: defaultUnitId || "",
    contract_id: "",
    payment_responsibility: "" as PaymentResponsibility | "",
    expense_type: "" as ExpenseType | "",
    amount: "",
    vendor_name: "",
    description: "",
  });

  // Sync defaults when dialog opens
  useEffect(() => {
    if (open && !expense) {
      setFormData(prev => ({
        ...prev,
        property_id: defaultPropertyId || prev.property_id,
        unit_id: defaultUnitId || prev.unit_id,
      }));
    }
    if (!open) {
      setInvoiceFile(null);
      setInvoiceUrl("");
      setImageFiles([]);
      setImageUrls([]);
    }
  }, [open, defaultPropertyId, defaultUnitId, expense]);

  // Pre-fill when editing
  useEffect(() => {
    if (open && expense) {
      setFormData({
        property_id: expense.property_id || "",
        unit_id: expense.unit_id || "",
        contract_id: expense.contract_id || "",
        payment_responsibility: expense.payment_responsibility || "",
        expense_type: expense.expense_type || "",
        amount: String(expense.amount || ""),
        vendor_name: expense.vendor_name || "",
        description: expense.description || "",
      });
      setDate(expense.expense_date ? new Date(expense.expense_date) : new Date());
      const storedVatRate = expense.vat_rate ?? 0;
      setIncludeVat(storedVatRate > 0);
      // Prefill the actual rate so the edit preserves what was originally stored.
      // When VAT was off (rate=0) we keep vatRateValue at 15 ready for if the
      // user turns VAT on during the edit session.
      setVatRateValue(storedVatRate > 0 ? storedVatRate : 15);
      setInvoiceUrl(expense.invoice_url || "");
      setInvoiceFile(null);
      const imgs = Array.isArray(expense.images) ? (expense.images as string[]) : [];
      setImageUrls(imgs);
      setImageFiles([]);
    }
  }, [open, expense]);

  // Fetch properties from database
  const { data: properties = [], isLoading: propertiesLoading } = useProperties();
  
  // Fetch units filtered by selected property
  const { data: allUnits = [], isLoading: unitsLoading } = useUnits(formData.property_id || undefined);
  
  // Fetch contracts
  const { data: allContracts = [], isLoading: contractsLoading } = useContracts();

  // Filter units by selected property
  const filteredUnits = formData.property_id 
    ? allUnits.filter(unit => unit.property_id === formData.property_id)
    : [];

  // Filter contracts by selected unit (only active contracts)
  const filteredContracts = formData.unit_id 
    ? allContracts.filter(contract => 
        contract.unit_id === formData.unit_id && 
        contract.status === "active"
      )
    : [];

  // Reset dependent fields when parent changes
  useEffect(() => {
    if (!formData.property_id) {
      setFormData(prev => ({ ...prev, unit_id: "", contract_id: "" }));
    }
  }, [formData.property_id]);

  useEffect(() => {
    if (!formData.unit_id) {
      setFormData(prev => ({ ...prev, contract_id: "" }));
    }
  }, [formData.unit_id]);

  const createExpense = useCreateExpense();
  const updateExpense = useUpdateExpense();

  const amount = parseFloat(formData.amount) || 0;
  const vatAmount = includeVat ? amount * (vatRateValue / 100) : 0;
  const total = amount + vatAmount;

  // Handle invoice file upload
  const handleInvoiceUpload = useCallback(async (file: File) => {
    if (!companyId) {
      toast.error(t("خطأ في الجلسة، يرجى إعادة تحميل الصفحة", "Session error, please reload the page"));
      return;
    }
    setIsUploadingInvoice(true);
    try {
      // "maintenance" is a PRIVATE folder (#3059 C1 phase 2 — expense
      // invoices are financial PII) — persist the bare path, never the
      // transient signed url.
      const result = await uploadDocument(file, "maintenance", companyId);
      setInvoiceUrl(result.path);
      setInvoiceFile(file);
      toast.success(t("تم رفع الفاتورة بنجاح", "Invoice uploaded successfully"));
    } catch (error) {
      // User-input validation errors (wrong MIME type, oversized file, duplicate)
      // are expected — skip console.error so Sentry's captureConsoleIntegration
      // doesn't forward them as real application errors (#3080).
      if (!isExpectedUploadError(error)) console.error("Invoice upload error:", error);
      const msg = getStorageErrorMessage(error, {
        ar: "فشل رفع الفاتورة",
        en: "Failed to upload invoice",
      });
      toast.error(t(msg.ar, msg.en), { duration: 8000 });
    } finally {
      setIsUploadingInvoice(false);
    }
  }, [t, companyId]);

  // Handle images upload
  const handleImagesUpload = useCallback(async (files: File[]) => {
    if (!companyId) {
      toast.error(t("خطأ في الجلسة، يرجى إعادة تحميل الصفحة", "Session error, please reload the page"));
      return;
    }
    setIsUploadingImages(true);
    try {
      // "maintenance" is a PRIVATE folder — persist bare paths, not urls.
      const results = await uploadImages(files, "maintenance", companyId);
      const newUrls = results.map(r => r.path);
      setImageUrls(prev => [...prev, ...newUrls]);
      setImageFiles(prev => [...prev, ...files]);
      toast.success(t("تم رفع الصور بنجاح", "Images uploaded successfully"));
    } catch (error) {
      // User-input validation errors (wrong MIME type, oversized file, duplicate)
      // are expected — skip console.error so Sentry's captureConsoleIntegration
      // doesn't forward them as real application errors (#3080).
      if (!isExpectedUploadError(error)) console.error("Image upload error:", error);
      const msg = getStorageErrorMessage(error, {
        ar: "فشل رفع الصور",
        en: "Failed to upload images",
      });
      toast.error(t(msg.ar, msg.en), { duration: 8000 });
    } finally {
      setIsUploadingImages(false);
    }
  }, [t, companyId]);

  // Drag and drop handlers for invoice
  const handleInvoiceDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingInvoice(true);
  }, []);

  const handleInvoiceDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingInvoice(false);
  }, []);

  const handleInvoiceDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingInvoice(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleInvoiceUpload(files[0]);
    }
  }, [handleInvoiceUpload]);

  // Drag and drop handlers for images
  const handleImagesDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingImages(true);
  }, []);

  const handleImagesDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingImages(false);
  }, []);

  const handleImagesDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingImages(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleImagesUpload(files);
    }
  }, [handleImagesUpload]);

  // Remove uploaded image
  const removeImage = (index: number) => {
    setImageUrls(prev => prev.filter((_, i) => i !== index));
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Remove uploaded invoice
  const removeInvoice = () => {
    setInvoiceFile(null);
    setInvoiceUrl("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.expense_type || !formData.payment_responsibility || !formData.description || amount <= 0) {
      return;
    }

    if (isEdit && expense) {
      await updateExpense.mutateAsync({
        id: expense.id,
        expense_date: date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        expense_type: formData.expense_type as ExpenseType,
        payment_responsibility: formData.payment_responsibility as PaymentResponsibility,
        amount: amount,
        vat_rate: includeVat ? vatRateValue : 0,
        vendor_name: formData.vendor_name || undefined,
        description: formData.description,
        invoice_url: invoiceUrl || undefined,
        images: imageUrls.length > 0 ? imageUrls : undefined,
      });
    } else {
      await createExpense.mutateAsync({
        property_id: formData.property_id || undefined,
        unit_id: formData.unit_id || undefined,
        contract_id: formData.contract_id || undefined,
        expense_date: date ? format(date, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd"),
        expense_type: formData.expense_type as ExpenseType,
        payment_responsibility: formData.payment_responsibility as PaymentResponsibility,
        amount: amount,
        vat_rate: includeVat ? vatRateValue : 0,
        vendor_name: formData.vendor_name || undefined,
        description: formData.description,
        invoice_url: invoiceUrl || undefined,
        images: imageUrls.length > 0 ? imageUrls : undefined,
      });
    }

    // Reset form
    setFormData({
      property_id: "",
      unit_id: "",
      contract_id: "",
      payment_responsibility: "",
      expense_type: "",
      amount: "",
      vendor_name: "",
      description: "",
    });
    setDate(new Date());
    setIncludeVat(false);
    setVatRateValue(15);
    setInvoiceFile(null);
    setInvoiceUrl("");
    setImageFiles([]);
    setImageUrls([]);
    onOpenChange(false);
  };

  const handlePropertyChange = (value: string) => {
    setFormData({ 
      ...formData, 
      property_id: value,
      unit_id: "",
      contract_id: ""
    });
  };

  const handleUnitChange = (value: string) => {
    setFormData({ 
      ...formData, 
      unit_id: value,
      contract_id: ""
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("تعديل مصروف", "Edit Expense") : t("إضافة مصروف", "Add Expense")}</DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit
              ? t("عدّل بيانات المصروف بما في ذلك النوع والمبلغ والتاريخ والمسؤولية عن الدفع", "Edit the expense details including type, amount, date, and payment responsibility")
              : t("أضف مصروفاً جديداً بتحديد نوعه والمبلغ والتاريخ والمسؤولية عن الدفع", "Add a new expense by specifying its type, amount, date, and payment responsibility")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Property - Connected to database */}
            <div className="space-y-2">
              <Label>{t("العقار", "Property")}</Label>
              <SearchableSelect
                items={properties}
                value={formData.property_id}
                onValueChange={(v) => handlePropertyChange(v ?? "")}
                getKey={(p) => p.id}
                getLabel={(p) => `${p.name}${p.deed_number ? ` (${p.deed_number})` : ""}`}
                getSubtitle={(p) => p.district || null}
                getSearchString={(p) =>
                  `${p.name ?? ""} ${p.name_en ?? ""} ${p.deed_number ?? ""} ${p.district ?? ""}`
                }
                placeholder={
                  propertiesLoading
                    ? t("جاري التحميل...", "Loading...")
                    : t("اختر العقار", "Select property")
                }
                searchPlaceholder={t("ابحث باسم العقار، الصك، الحي", "Search by name, deed, district")}
                emptyText={t("لا توجد عقارات مطابقة", "No matching property")}
                disabled={propertiesLoading}
              />
            </div>

            {/* Unit - Filtered by property */}
            <div className="space-y-2">
              <Label>{t("الوحدة", "Unit")}</Label>
              <SearchableSelect
                items={filteredUnits}
                value={formData.unit_id}
                onValueChange={(v) => handleUnitChange(v ?? "")}
                getKey={(u) => u.id}
                getLabel={(u) => getUnitDisplayLabel(u, language === "ar" ? "ar" : "en")}
                placeholder={
                  !formData.property_id
                    ? t("اختر العقار أولاً", "Select property first")
                    : unitsLoading
                      ? t("جاري التحميل...", "Loading...")
                      : t("اختر الوحدة", "Select unit")
                }
                searchPlaceholder={t("ابحث برقم الوحدة", "Search by unit number")}
                emptyText={t("لا توجد وحدات", "No units available")}
                disabled={!formData.property_id || unitsLoading}
              />
            </div>

            {/* Contract - Filtered by unit (active only) */}
            <div className="space-y-2">
              <Label>{t("العقد", "Contract")}</Label>
              <Select
                value={formData.contract_id}
                onValueChange={(value) => setFormData({ ...formData, contract_id: value })}
                disabled={!formData.unit_id || contractsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.unit_id 
                      ? t("اختر الوحدة أولاً", "Select unit first")
                      : contractsLoading 
                        ? t("جاري التحميل...", "Loading...")
                        : t("اختر العقد", "Select contract")
                  } />
                </SelectTrigger>
                <SelectContent>
                  {filteredContracts.map((contract) => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.contract_number || contract.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                  {filteredContracts.length === 0 && formData.unit_id && !contractsLoading && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      {t("لا توجد عقود نشطة", "No active contracts")}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Payment Responsibility - Required with Owner/Tenant/Company */}
            <div className="space-y-2">
              <Label>{t("مسؤولية الدفع", "Payment responsibility")} *</Label>
              <Select
                value={formData.payment_responsibility}
                onValueChange={(value) => setFormData({ ...formData, payment_responsibility: value as PaymentResponsibility })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("اختر مسؤولية الدفع", "Select payment responsibility")} />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_RESPONSIBILITIES.map((resp) => (
                    <SelectItem key={resp.value} value={resp.value}>
                      {t(resp.labelAr, resp.labelEn)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Expense Type */}
            <div className="space-y-2">
              <Label>{t("نوع المصروف", "Expense type")} *</Label>
              <Select
                value={formData.expense_type}
                onValueChange={(value) => setFormData({ ...formData, expense_type: value as ExpenseType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("اختر نوع المصروف", "Select expense type")} />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(type.labelAr, type.labelEn)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>{t("التاريخ", "Date")} *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-start font-normal",
                      !date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="me-2 h-4 w-4" />
                    {date ? format(date, "dd/MM/yyyy") : t("اختر التاريخ", "DD/MM/YYYY")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => d && setDate(d)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>{t("المبلغ", "Amount")} *</Label>
              <Input
                type="number"
                placeholder={t("أدخل المبلغ", "Enter amount")}
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>

            {/* Total with VAT checkbox */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>{t("الإجمالي", "Total")}</Label>
                <Checkbox
                  id="vat"
                  checked={includeVat}
                  onCheckedChange={(checked) => setIncludeVat(checked as boolean)}
                />
                <Label htmlFor="vat" className="text-sm font-normal cursor-pointer">
                  {t("ضريبة القيمة المضافة", "VAT")} (15%)
                </Label>
              </div>
              <Input
                type="text"
                value={total.toFixed(2)}
                disabled
                className="bg-muted"
              />
              {includeVat && (
                <p className="text-xs text-muted-foreground">
                  {t("الضريبة", "VAT")}: {vatAmount.toFixed(2)}
                </p>
              )}
            </div>

            {/* Vendor Name */}
            <div className="space-y-2">
              <Label>{t("اسم المورد", "Vendor name")}</Label>
              <Input
                placeholder={t("أدخل اسم المورد", "Enter vendor name")}
                value={formData.vendor_name}
                onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>{t("الوصف", "Description")} *</Label>
            <Textarea
              placeholder={t("أدخل الوصف", "Enter description")}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
            />
          </div>

          {/* Invoice Upload */}
          <div className="space-y-2">
            <Label>{t("الفاتورة", "Invoice")}</Label>
            <input
              ref={invoiceInputRef}
              type="file"
              accept={IMAGE_OR_DOCUMENT_ACCEPT}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleInvoiceUpload(file);
              }}
              className="hidden"
            />
            
            {invoiceFile || invoiceUrl ? (
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-primary" />
                    <div>
                      <p className="text-sm font-medium truncate max-w-[200px]">
                        {invoiceFile ? invoiceFile.name : invoiceUrl.split("/").pop() || t("فاتورة موجودة", "Existing invoice")}
                      </p>
                      {invoiceFile && (
                        <p className="text-xs text-muted-foreground">
                          {(invoiceFile.size / 1024).toFixed(1)} KB
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={removeInvoice}
                    className="text-destructive hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onDragOver={handleInvoiceDragOver}
                onDragLeave={handleInvoiceDragLeave}
                onDrop={handleInvoiceDrop}
                onClick={() => invoiceInputRef.current?.click()}
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                  isDraggingInvoice ? "border-primary bg-primary/5" : "hover:border-primary"
                )}
              >
                {isUploadingInvoice ? (
                  <Loader2 className="h-8 w-8 mx-auto mb-2 text-primary animate-spin" />
                ) : (
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                )}
                <p className="text-sm text-muted-foreground">
                  {isUploadingInvoice 
                    ? t("جاري الرفع...", "Uploading...")
                    : t("اسحب وأفلت هنا، أو تصفح", "Drag & drop here, or browse")}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, PNG, JPG, DOC (max 10MB)
                </p>
              </div>
            )}
          </div>

          {/* Images Upload */}
          <div className="space-y-2">
            <Label>{t("الصور", "Images")}</Label>
            <input
              ref={imagesInputRef}
              type="file"
              multiple
              accept={IMAGE_ACCEPT}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) handleImagesUpload(files);
              }}
              className="hidden"
            />
            
            {/* Preview uploaded images */}
            {imageUrls.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-3">
                {imageUrls.map((path, index) => (
                  <ExpenseImagePreviewThumb
                    key={index}
                    path={path}
                    onRemove={() => removeImage(index)}
                  />
                ))}
              </div>
            )}
            
            <div
              onDragOver={handleImagesDragOver}
              onDragLeave={handleImagesDragLeave}
              onDrop={handleImagesDrop}
              onClick={() => imagesInputRef.current?.click()}
              className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                isDraggingImages ? "border-primary bg-primary/5" : "hover:border-primary"
              )}
            >
              {isUploadingImages ? (
                <Loader2 className="h-8 w-8 mx-auto mb-2 text-primary animate-spin" />
              ) : (
                <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              )}
              <p className="text-sm text-muted-foreground">
                {isUploadingImages 
                  ? t("جاري الرفع...", "Uploading...")
                  : t("اسحب وأفلت هنا، أو تصفح", "Drag & drop here, or browse")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PNG, JPG, GIF, WebP (max 10MB each)
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full"
            disabled={createExpense.isPending || updateExpense.isPending || !formData.expense_type || !formData.payment_responsibility || !formData.description || amount <= 0}
          >
            {(createExpense.isPending || updateExpense.isPending) ? (
              <>
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
                {t("جاري الحفظ...", "Saving...")}
              </>
            ) : (
              t("تأكيد", "Confirm")
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Resolves a single expense image's storage reference to a render-ready URL
 * for the in-dialog preview grid. #3059 C1 phase 2: "maintenance" is a
 * PRIVATE folder — `path` is a bare `documents`-bucket path (both for a
 * freshly-uploaded file in this session and for a pre-filled edit), or a
 * legacy full public `media` URL for pre-migration rows. resolveDocumentUrl
 * handles both. Mirrors ExpenseDetailsModal's ExpenseImageThumb.
 */
function ExpenseImagePreviewThumb({ path, onRemove }: { path: string; onRemove: () => void }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    resolveDocumentUrl(path)
      .then((resolved) => {
        if (!cancelled) setUrl(resolved);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return (
    <div className="relative aspect-square rounded-lg overflow-hidden border group">
      {url ? (
        <img src={url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-muted animate-pulse" />
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1 end-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
